import React from 'react';
import {Box, Typography, Divider, Chip, IconButton} from '@mui/material';
import LinkIcon from '$assets/svgs/Link.svg?react';
import StripeIcon from '$assets/svgs/Stripe.svg?react';
import PayPalIcon from '$assets/svgs/paypal-icon.svg?react';
import BankNoteIcon from '$assets/svgs/bank-note.svg?react';
import CreditCardIcon from '$assets/svgs/credit-card-x.svg?react';
import BankIcon from '$assets/svgs/bank.svg?react';
import {
  summaryContainerStyles,
  summaryBoxStyles,
  summaryLabelStyles,
  summaryValueStyles,
  summarySmallValueStyles,
  verticalDividerStyles,
  paymentMethodContainerStyles,
  cardIconStyles,
  centeredSummaryValueStyles,
  chequeIconStyles,
  paymentLinkHeaderStyles,
  paymentLinkIconButtonStyles,
  paymentLinkTextStyles,
} from '../../Finance/PaymentDetails/styles';

import {getStatusChipStyles} from '$modules/frontdesk/utils';
import {
  formatCurrency,
  PaymentStatusInput,
  getPaymentStatusType,
  getPaymentStatusDisplay,
} from '$modules/frontdesk/utils';
import toast from '~/utils/toast';
import {ToastType} from '~/components/Toast/constant';

export interface PaymentSummaryProps {
  amount: string;
  status: PaymentStatusInput;
  paymentMethodType: string;
  invoiceNumber: string;
  lastUpdate: string;
  dateLabel?: string;
  updatedAmount?: string;
  discount?: number | null;
  paymentLink?: string;
}

const PaymentSummary: React.FC<PaymentSummaryProps> = ({
  amount,
  status,
  paymentMethodType,
  invoiceNumber,
  lastUpdate,
  dateLabel,
  updatedAmount,
  discount,
  paymentLink,
}) => {
  const statusType = getPaymentStatusType(status);
  const statusDisplay = getPaymentStatusDisplay(status);
  const formattedDate = new Date(lastUpdate).toLocaleDateString();
  const isPaymentLinkMethod =
    paymentMethodType === 'payment_link' ||
    paymentMethodType === 'payment link';

  let amountToDisplay = amount;

  if (updatedAmount) {
    amountToDisplay = updatedAmount;
  }

  const handleCopyPaymentLink = async () => {
    if (!paymentLink) return;

    try {
      await navigator.clipboard.writeText(paymentLink);
      toast(ToastType.Success, 'Success', 'Payment link copied to clipboard');
    } catch {
      toast(ToastType.Error, 'Error', 'Failed to copy payment link');
    }
  };

  const handlePaymentLinkClick = () => {
    if (paymentLink) {
      window.open(paymentLink, '_blank');
    }
  };

  return (
    <Box sx={summaryContainerStyles}>
      <Box sx={summaryBoxStyles}>
        <Typography sx={summaryLabelStyles}>Amount</Typography>
        <Typography sx={summaryValueStyles}>
          {formatCurrency(Number(amountToDisplay))}
        </Typography>
      </Box>
      <Divider orientation="vertical" flexItem sx={verticalDividerStyles} />
      <Box sx={summaryBoxStyles}>
        <Typography sx={summaryLabelStyles}>Status</Typography>
        <Box sx={centeredSummaryValueStyles}>
          <Chip label={statusDisplay} sx={getStatusChipStyles(statusType)} />
        </Box>
      </Box>
      <Divider orientation="vertical" flexItem sx={verticalDividerStyles} />
      <Box sx={summaryBoxStyles}>
        <Typography sx={summaryLabelStyles}>
          {dateLabel || 'Last Update'}
        </Typography>
        <Typography sx={summarySmallValueStyles}>{formattedDate}</Typography>
      </Box>
      <Divider orientation="vertical" flexItem sx={verticalDividerStyles} />
      <Box sx={summaryBoxStyles}>
        <Typography sx={summaryLabelStyles}>Payment Method</Typography>
        <Box sx={paymentMethodContainerStyles}>
          {paymentMethodType === 'stripe' ? (
            <Box sx={cardIconStyles}>
              <StripeIcon />
            </Box>
          ) : paymentMethodType === 'card' ? (
            <Box sx={paymentMethodContainerStyles}>
              <CreditCardIcon />
              <Typography>Card</Typography>
            </Box>
          ) : paymentMethodType === 'cash' ? (
            <Box sx={paymentMethodContainerStyles}>
              <BankNoteIcon />
              <Typography>Cash</Typography>
            </Box>
          ) : paymentMethodType === 'cheque' ? (
            <Box sx={paymentMethodContainerStyles}>
              <BankIcon style={chequeIconStyles} />
              <Typography>Cheque</Typography>
            </Box>
          ) : paymentMethodType === 'paypal' ? (
            <Box sx={paymentMethodContainerStyles}>
              <PayPalIcon />
              <Typography>PayPal</Typography>
            </Box>
          ) : paymentMethodType === 'payment_link' ||
            paymentMethodType === 'payment link' ? (
            <Box sx={paymentMethodContainerStyles}>
              <LinkIcon />
              <Typography>Payment Link</Typography>
            </Box>
          ) : (
            <Typography sx={summarySmallValueStyles}>
              {paymentMethodType}
            </Typography>
          )}
        </Box>
      </Box>
      <Divider orientation="vertical" flexItem sx={verticalDividerStyles} />
      <Box sx={summaryBoxStyles}>
        <Typography sx={summaryLabelStyles}>Invoice Number</Typography>
        <Typography sx={summarySmallValueStyles}>{invoiceNumber}</Typography>
      </Box>

      {discount !== null && (
        <>
          <Divider orientation="vertical" flexItem sx={verticalDividerStyles} />
          <Box sx={summaryBoxStyles}>
            <Typography sx={summaryLabelStyles}>Discount</Typography>
            <Typography sx={summarySmallValueStyles} color="error">
              -{formatCurrency(discount || 0)}
            </Typography>
          </Box>
        </>
      )}

      {paymentLink && isPaymentLinkMethod && (
        <>
          <Divider orientation="vertical" flexItem sx={verticalDividerStyles} />
          <Box sx={summaryBoxStyles}>
            <Box sx={paymentLinkHeaderStyles}>
              <Typography sx={summaryLabelStyles}>Payment Link</Typography>
              <IconButton
                size="small"
                onClick={handleCopyPaymentLink}
                sx={paymentLinkIconButtonStyles}
              >
                <LinkIcon />
              </IconButton>
            </Box>
            <Typography
              sx={{
                ...summarySmallValueStyles,
                ...paymentLinkTextStyles,
              }}
              onClick={handlePaymentLinkClick}
            >
              {paymentLink && paymentLink.length > 30
                ? `${paymentLink.substring(0, 30)}...`
                : paymentLink}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PaymentSummary;
